/**
 * What: Shared payload and value formatting utilities for results views.
 * Transport / data-flow: Formats raw payload values (strings, numbers, objects, arrays) into clean display strings.
 *
 */

/**
 * Formats a recommendation payload value into a user-friendly display string.
 */
export function formatPayloadValue(value: unknown): string {
  if (value === null || value === undefined) return 'None'
  if (typeof value === 'string') return value || 'None'
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * Formats an ISO timestamp or date value into a standard localized medium date with short time.
 */
export function formatDateTime(value: string | number | Date | null | undefined, fallback = 'None'): string {
  if (!value) return fallback
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return typeof value === 'string' ? value : fallback
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

/**
 * Names an IANA time zone for people ("America/New_York" -> "Eastern Time"). Returns null when the
 * runtime cannot name it, so the caller shows nothing rather than the raw identifier.
 */
export function formatTimeZoneName(timeZone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'longGeneric' }).formatToParts(
      new Date(),
    )
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? null
  } catch (error) {
    console.error('[formatTimeZoneName]', error)
    return null
  }
}
